import React, { MouseEvent } from 'react';
import { PureComponent } from '../../../dispatcher/modules';
import HBox from '../../layout/container/h_box/HBox';
import {
  GRID_COL_INDEX_ATTRIBUTE
} from '@progress/kendo-react-grid/dist/npm/constants';
import WithTooltip from '../../../utility/withTooltip';
import PuzzleTooltip from '../../UI/tooltip/PuzzleTooltip';
import { TooltipPosition } from '@progress/kendo-react-tooltip';
import _, { isPlainObject } from 'lodash';
import { isElNotLinkButtonOrInput } from '../../../utility';
import { CELL_ABSTRACT_PROPS, CELL_CONFIG_ABSTRACT } from './Types';

class CellAbstract<P = {}, S = {}> extends PureComponent<P & CELL_ABSTRACT_PROPS, S> {
  tooltip: any = null;

  private assignTooltipRef = (el: any) => {
    this.tooltip = el;
  };

  renderAlign = (children: React.ReactNode, align?: CELL_CONFIG_ABSTRACT) => {
    if (align) {
      const props = {
        justifyContentCenter: align === 'center',
        justifyContentEnd: align === 'right',
        justifyContentStart: align === 'left'
      };
      children = (
        <HBox {...props}>
          {children}
        </HBox>
      );
    }
    return children;
  };

  renderCell = (children: React.ReactNode, tooltip?: any) => {
    const {
      cellConfig = {},
      htmlTag
    } = this.props;
    const { align, htmlTagCls } = (cellConfig as any);

    children = this.renderTooltip(children, tooltip);

    children = this.renderAlign(children, align);

    return (htmlTag === 'div') ? children : this.renderWrapper(children, htmlTagCls);
  };

  renderTooltip = (children: React.ReactNode, tooltip?: any) => {
    if (tooltip) {
      let title = tooltip;
      let position = 'top';
      let content;

      if (isPlainObject(tooltip)) {
        ({ content, title, position } = tooltip);
      }

      children = (
        <>
          <WithTooltip
            context={this}
            htmlTag="span"
            title={title}
          >
            {children}
          </WithTooltip>
          <PuzzleTooltip
            anchorElement="target"
            content={content}
            openDelay={1000}
            position={position as TooltipPosition}
            ref={this.assignTooltipRef}
          />
        </>
      );
    }

    return children;
  };

  /**
   * Handler, used for handling grid selection.
   * If the `onClick` parameter is passed through props, and the target isn't td, button, input, or link. Then will call onClick method.
   * @param {MouseEvent} event
   */
  private onCellClick = (event: MouseEvent):void => {
    const { onClick, dataItem, onRowDoubleClick } = this.props;
    const nodeType: string = _.get(event.target, 'nodeName', '');

    if (_.isFunction(onClick) && isElNotLinkButtonOrInput(nodeType) && nodeType !== 'TD') {
      const e = {
        nativeEvent: event.nativeEvent,
        dataItem,
        target: event.target,
        syntheticEvent: event
      };

      onClick(e);
      const isDoubleClick = event.nativeEvent?.detail > 1;

      if (isDoubleClick) {
        onRowDoubleClick(e);
      }
    }
  }

  private onFocus = (event) => {
    const { additionalOnFocus, dataItem, cellField } = this.props;
    if (additionalOnFocus) {
      additionalOnFocus(dataItem, cellField, event);
    }
  }

  renderWrapper = (children: React.ReactNode, htmlTagCls: string = '') => {
    let {
      colSpan,
      ariaColumnIndex,
      className,
      columnIndex,
      isSelected,
      htmlTag = 'td',
      style,
      cellConfig,
      editable
    } = this.props;
    let tagClassName = className as string | undefined;
    const Tag = htmlTag;

    if (htmlTagCls) {
      const blank = className ? ' ' : '';
      tagClassName = `${tagClassName} ${blank} ${htmlTagCls}`;
    }

    if (cellConfig) {
      const {
        parentClassName = '',
        noSpan
      } = cellConfig;
      tagClassName = `${tagClassName} ${noSpan ? 'tp-text-ellipsis' : ''} ${parentClassName}`;
    }
    
    return (
      <Tag
        aria-colindex={ariaColumnIndex}
        aria-selected={isSelected}
        className={tagClassName}
        colSpan={colSpan}
        role="gridcell"
        style={style}
        onClick={this.onCellClick}
        onFocus={this.onFocus}
        {...{ [GRID_COL_INDEX_ATTRIBUTE]: columnIndex }}
        {...(cellConfig?.additionalProps || {})}
        tabIndex={editable ? 0 : undefined}
      >
        {children}
      </Tag>
    );
  };
}

export default CellAbstract;
